import moment from "moment";
import { getNonOperationalByLevels, getNonOperationalSummaryCards } from "@/api/financialReport";
import { consoleHandlerError, extractErrorMessage } from "@/utils/message";
import { createTakeLatest } from "@/utils/takeLatest";
import { resolveDateRange, buildHeadersByPeriod, formatPeriodLabel } from "./utils/dateRange";

// Prefijos para distinguir signo del level_2 al netear en totalRow/ratioRow.
// El backend entrega ambos amounts como POSITIVOS (semánticamente "ingresos
// no operacionales positivos" y "gastos no operacionales positivos") — el
// módulo aplica el signo según el prefijo del label.
const INCOME_LABEL_PREFIX = "ingreso";

const isIncomeLabel = (label) => (label || "").toLowerCase().startsWith(INCOME_LABEL_PREFIX);

// Take-latest por action (mismo patrón que classification.js y que
// createBalanceLevelReport): sólo el fetch más reciente commitea — una
// respuesta vieja que llega tarde no clobbea estado fresco.
const runLatestData = createTakeLatest();
const runLatestSummary = createTakeLatest();

const state = () => ({
    selectedCounterpartTypes: [],
    monthHeaders: [],
    accountsData: {},
    level2Accounts: [],
    level3Accounts: [],
    hierarchy: {},
    salesByPeriod: {},
    /**
     * Acumulado del periodo + comparación contra ventana previa de igual largo.
     * Indexado por L2/L3. NO usamos la clave "__total__" del backend porque
     * éste suma todos los L2 en positivo; el neto signed (ingresos - gastos)
     * se reconstruye en el getter totalRow con prefix matching.
     * @type {Object.<string, { acc: number, prevAcc: number, pct: number | null }>}
     */
    accumulatedData: {},
    periodRange: { init_date: null, end_date: null },
    isLoading: false,
    error: null,
    summaryCards: null,
    isSummaryLoading: false,
    summaryError: null,
});

const EMPTY_ACC = { acc: 0, prevAcc: 0, pct: null };

const getters = {
    isCuentasMode: (state) => state.selectedCounterpartTypes.length === 0,

    groupByParam: (state) => {
        if (state.selectedCounterpartTypes.length === 0) return "cuentas";
        return state.selectedCounterpartTypes.join(",");
    },

    tableRows: (state) => {
        const rows = [];
        state.level2Accounts.forEach(l2 => {
            // accumulated/pct vienen del backend en valor absoluto positivo
            // (mismo signo que values), no se invierten por prefix — la fila
            // muestra magnitud del ingreso o gasto, no su contribución neta.
            const l2Acc = state.accumulatedData[l2] ?? EMPTY_ACC;
            rows.push({
                label: l2,
                level: 2,
                isTotal: false,
                values: state.monthHeaders.map(
                    (header) => state.accountsData[l2]?.[header] ?? 0
                ),
                accumulated: l2Acc.acc,
                pct: l2Acc.pct,
            });

            const children = state.hierarchy[l2] || [];
            children.forEach(l3 => {
                if (l3 === l2) return;
                const l3Acc = state.accumulatedData[l3] ?? EMPTY_ACC;
                rows.push({
                    label: l3,
                    level: 3,
                    parent: l2,
                    isTotal: false,
                    values: state.monthHeaders.map(
                        (header) => state.accountsData[l3]?.[header] ?? 0
                    ),
                    accumulated: l3Acc.acc,
                    pct: l3Acc.pct,
                });
            });
        });
        return rows;
    },

    // Total No Operacional = Σ ingresos - Σ gastos. A diferencia del resto de
    // tabs (que suman todos los level_2), acá distinguimos signo por prefijo
    // del label porque ambos magnitudes vienen positivas del backend.
    // El __total__ del backend NO sirve acá (suma todo positivo); reconstruimos
    // el neto signed sobre los L2 y recalculamos pct con prev neteado.
    totalRow: (state) => {
        const values = state.monthHeaders.map((header) =>
            state.level2Accounts.reduce((sum, account) => {
                const val = Number(state.accountsData[account]?.[header] || 0);
                if (isNaN(val)) return sum;
                return isIncomeLabel(account) ? sum + val : sum - val;
            }, 0)
        );
        const netAcc = state.level2Accounts.reduce((sum, account) => {
            const a = state.accumulatedData[account]?.acc ?? 0;
            return isIncomeLabel(account) ? sum + a : sum - a;
        }, 0);
        const netPrev = state.level2Accounts.reduce((sum, account) => {
            const p = state.accumulatedData[account]?.prevAcc ?? 0;
            return isIncomeLabel(account) ? sum + p : sum - p;
        }, 0);
        const pct = netPrev !== 0 ? netAcc / netPrev - 1 : null;
        return {
            label: "Total No Operacionales",
            values,
            isTotal: true,
            accumulated: netAcc,
            pct,
        };
    },

    // Ratio No Operacional / Ventas. Numerador = neto signed (positivo si
    // ingresos > gastos). Denominador = ventas netas del período.
    ratioRow: (state) => {
        const values = state.monthHeaders.map((header) => {
            const neto = state.level2Accounts.reduce((sum, account) => {
                const val = Number(state.accountsData[account]?.[header] || 0);
                if (isNaN(val)) return sum;
                return isIncomeLabel(account) ? sum + val : sum - val;
            }, 0);
            const ventas = Number(state.salesByPeriod[header] || 0);
            return ventas !== 0 ? neto / ventas : 0;
        });
        return { label: "No Operacional / Ventas (%)", values, isRatio: true };
    },
};

const mutations = {
    SET_SELECTED_COUNTERPART_TYPES(state, types) {
        state.selectedCounterpartTypes = Array.isArray(types) ? [...types] : [];
    },
    SET_NON_OPERATIONAL_DATA(state, { level2Accounts, level3Accounts, hierarchy, accountsData, monthHeaders, periodRange, salesByPeriod, accumulatedData }) {
        state.level2Accounts = level2Accounts;
        state.level3Accounts = level3Accounts;
        state.hierarchy = hierarchy;
        state.accountsData = accountsData;
        state.monthHeaders = monthHeaders;
        state.periodRange = periodRange;
        state.salesByPeriod = salesByPeriod || {};
        state.accumulatedData = accumulatedData || {};
    },
    SET_LOADING(state, isLoading) { state.isLoading = isLoading; },
    SET_ERROR(state, error) { state.error = error; },
    SET_SUMMARY_CARDS(state, summary) { state.summaryCards = summary; },
    SET_SUMMARY_LOADING(state, isLoading) { state.isSummaryLoading = isLoading; },
    SET_SUMMARY_ERROR(state, error) { state.summaryError = error; },
};

const actions = {
    async fetchNonOperationalData({ getters, commit, rootState }) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        let stale = false;
        try {
            const endMonth = rootState.reportsFilters.endMonth;
            const filterPeriod = rootState.financialReports.filterPeriod;

            // customRange/customYearRange incluidos: sin ellos, un rango
            // custom elegido por el usuario se ignoraba en este tab mientras
            // Resultados/Ventas sí lo honraban.
            const { initDate, endDate } = resolveDateRange(
                endMonth,
                rootState.reportsFilters.selectedPeriodRange,
                filterPeriod,
                rootState.reportsFilters.customRange,
                rootState.reportsFilters.customYearRange,
            );

            const result = await runLatestData((signal) =>
                getNonOperationalByLevels(initDate, endDate, getters.groupByParam, filterPeriod, { signal }),
            );
            stale = result.stale;
            if (stale) {
                // Error stale: se loguea (como antes) pero no pisa el estado
                // del fetch vigente.
                if (result.error) {
                    consoleHandlerError({ error: result.error, prefix: "[nonOperationalReport]", silent: true });
                }
                return;
            }
            const response = result.value;
            const {
                report_data,
                level2_accounts,
                level3_accounts,
                hierarchy,
                sales_by_period,
                accumulated_data: accumulatedRaw,
            } = response.data;

            const monthHeaders = buildHeadersByPeriod(initDate, endDate, filterPeriod);
            const accountsData = {};

            Object.keys(report_data).forEach(accountName => {
                accountsData[accountName] = {};
                report_data[accountName].forEach(row => {
                    const header = formatPeriodLabel(row.period, filterPeriod);
                    const amount = Number(row.total_amount);
                    accountsData[accountName][header] = isNaN(amount) ? 0 : amount;
                });
            });

            const salesByPeriod = {};
            (sales_by_period || []).forEach(row => {
                const header = formatPeriodLabel(row.period, filterPeriod);
                const amount = Number(row.total_amount);
                salesByPeriod[header] = isNaN(amount) ? 0 : amount;
            });

            // Normaliza snake→camel y castea a Number. Decimal puede llegar
            // como string desde algunos paths del selector.
            const accumulatedData = {};
            Object.entries(accumulatedRaw ?? {}).forEach(([key, value]) => {
                accumulatedData[key] = {
                    acc: Number(value?.acc ?? 0),
                    prevAcc: Number(value?.prev_acc ?? 0),
                    pct: value?.pct == null ? null : Number(value.pct),
                };
            });

            commit("SET_NON_OPERATIONAL_DATA", {
                level2Accounts: level2_accounts,
                level3Accounts: level3_accounts,
                hierarchy,
                accountsData,
                monthHeaders,
                periodRange: { init_date: initDate, end_date: endDate },
                salesByPeriod,
                accumulatedData,
            });
        } catch (error) {
            consoleHandlerError({ error, prefix: "[nonOperationalReport]", silent: true });
            commit("SET_ERROR", "Error al cargar no operacional.");
        } finally {
            if (!stale) commit("SET_LOADING", false);
        }
    },

    async fetchNonOperationalSummary({ commit, rootState }) {
        commit("SET_SUMMARY_LOADING", true);
        commit("SET_SUMMARY_ERROR", null);

        let stale = false;
        try {
            const endDate = moment(rootState.reportsFilters.endMonth)
                .endOf("month")
                .format("YYYY-MM-DD");

            const result = await runLatestSummary((signal) =>
                getNonOperationalSummaryCards(endDate, { signal }),
            );
            stale = result.stale;
            if (stale) {
                if (result.error) {
                    consoleHandlerError({ error: result.error, prefix: "[nonOperationalReport]", silent: true });
                }
                return;
            }
            commit("SET_SUMMARY_CARDS", Object.freeze(result.value.data));
        } catch (error) {
            consoleHandlerError({ error, prefix: "[nonOperationalReport]", silent: true });
            commit(
                "SET_SUMMARY_ERROR",
                extractErrorMessage(error, "Error al cargar resumen de no operacional.")
            );
        } finally {
            if (!stale) commit("SET_SUMMARY_LOADING", false);
        }
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
